import { useEffect, useState } from 'react';
import {
  ComboBox,
  Dropdown,
  Modal,
  RadioButtonGroup,
  RadioButton,
  TextInput,
} from '@carbon/react';
import style from './modals.module.scss';

interface ScenarioOption {
  id: number;
  label: string;
}

interface DpsOption {
  id: number;
  label: string;
}

interface EvaluationCaseOption {
  id: number;
  name: string;
  evaluationType: string;
}

type SourceMode = 'case' | 'dps';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (
    name: string,
    scenarioId?: number,
    dpsId?: number,
    evaluationCaseId?: number
  ) => void;
  scenarios: ScenarioOption[];
  dpsList?: DpsOption[];
  evaluationCaseList?: EvaluationCaseOption[];
  /** When true, hides the source dropdowns (already selected in main UI). */
  isEvalPreselected?: boolean;
}

/**
 * Modal shown when the user clicks the + button to create a new workspace.
 * Collects an optional workspace name, a Scenario, and either a Development
 * Pace Scenario or an Evaluation Case. If the name is left blank, the
 * backend auto-generates one.
 *
 * @param {CreateWorkspaceModalProps} props - Component props.
 * @return {JSX.Element} The create workspace modal.
 */
export const CreateWorkspaceModal = ({
  isOpen,
  isLoading,
  onClose,
  onSubmit,
  scenarios,
  dpsList = [],
  evaluationCaseList = [],
  isEvalPreselected = false,
}: CreateWorkspaceModalProps): JSX.Element => {
  const [name, setName] = useState('');
  const [sourceMode, setSourceMode] = useState<SourceMode>(
    evaluationCaseList.length > 0 ? 'case' : 'dps'
  );
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(
    null
  );
  const [selectedDpsId, setSelectedDpsId] = useState<number | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelectedScenarioId(null);
      setSelectedDpsId(null);
      setSelectedCaseId(null);
    }
  }, [isOpen]);

  const selectedScenarioItem =
    scenarios.find((s) => s.id === selectedScenarioId) ?? null;
  const selectedDpsItem = dpsList?.find((d) => d.id === selectedDpsId) ?? null;
  const selectedCaseItem =
    evaluationCaseList?.find((c) => c.id === selectedCaseId) ?? null;

  /**
   * Formats an evaluation case option for display in the ComboBox.
   * @param {EvaluationCaseOption | null} item - The case option
   * @return {string} Display string
   */
  const caseItemToString = (item: EvaluationCaseOption | null) =>
    item ? `${item.name} (${item.evaluationType})` : '';

  const handleSubmit = () => {
    if (isEvalPreselected) {
      onSubmit(name.trim());
    } else if (sourceMode === 'case') {
      onSubmit(
        name.trim(),
        selectedScenarioId ?? undefined,
        undefined,
        selectedCaseId ?? undefined
      );
    } else {
      onSubmit(
        name.trim(),
        selectedScenarioId ?? undefined,
        selectedDpsId ?? undefined,
        undefined
      );
    }
  };

  const hasSource =
    isEvalPreselected ||
    (selectedScenarioId !== null &&
      (sourceMode === 'case'
        ? selectedCaseId !== null
        : selectedDpsId !== null));
  const isSubmitDisabled = isLoading || !hasSource;

  const buttonLabel = isEvalPreselected ? 'Create New Workspace' : 'Create';

  return (
    <Modal
      open={isOpen}
      modalHeading={isEvalPreselected ? 'New Workspace' : 'Create Workspace'}
      primaryButtonText={isLoading ? 'Creating...' : buttonLabel}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={isSubmitDisabled}
      onRequestSubmit={handleSubmit}
      onRequestClose={isLoading ? undefined : onClose}
      className={style.createModal}
    >
      <p className={style.description}>
        Create a new, separate workspace to start with a clean slate.
      </p>
      <TextInput
        id="create-workspace-name"
        labelText="Workspace name (optional)"
        placeholder="Auto-generated if left blank"
        value={name}
        disabled={isLoading}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setName(e.target.value)
        }
      />
      {!isEvalPreselected && (
        <>
          <ComboBox<ScenarioOption>
            className={style.fieldGap}
            id="create-workspace-scenario"
            titleText="Scenario"
            placeholder="Select a Scenario"
            items={scenarios}
            selectedItem={selectedScenarioItem}
            disabled={isLoading}
            itemToString={(item: ScenarioOption | null) => item?.label ?? ''}
            shouldFilterItem={({
              item,
              inputValue,
            }: {
              item: ScenarioOption;
              inputValue: string | null;
            }) => {
              if (!inputValue) return true;
              return item.label
                .toLowerCase()
                .includes(inputValue.toLowerCase());
            }}
            onChange={({
              selectedItem: item,
            }: {
              selectedItem: ScenarioOption | null | undefined;
            }) => {
              setSelectedScenarioId(item?.id ?? null);
            }}
          />

          <RadioButtonGroup
            className={style.fieldGap}
            name="create-workspace-source-mode"
            legendText="Evaluation Source"
            valueSelected={sourceMode}
            onChange={(value) => setSourceMode(value as SourceMode)}
            orientation="horizontal"
          >
            <RadioButton
              id="source-mode-case"
              value="case"
              labelText="Evaluation Case"
            />
            <RadioButton
              id="source-mode-dps"
              value="dps"
              labelText="Eval 2.0 Base"
            />
          </RadioButtonGroup>

          {sourceMode === 'case' && evaluationCaseList.length > 0 && (
            <ComboBox<EvaluationCaseOption>
              className={style.fieldGap}
              id="create-workspace-eval-case"
              titleText="Evaluation Case"
              placeholder="Select an Evaluation Case"
              items={evaluationCaseList}
              selectedItem={selectedCaseItem}
              disabled={isLoading}
              itemToString={caseItemToString}
              shouldFilterItem={({
                item,
                inputValue,
              }: {
                item: EvaluationCaseOption;
                inputValue: string | null;
              }) => {
                if (!inputValue) return true;
                return caseItemToString(item)
                  .toLowerCase()
                  .includes(inputValue.toLowerCase());
              }}
              onChange={({
                selectedItem: item,
              }: {
                selectedItem: EvaluationCaseOption | null | undefined;
              }) => {
                setSelectedCaseId(item?.id ?? null);
              }}
            />
          )}

          {sourceMode === 'dps' && dpsList.length > 0 && (
            <Dropdown
              className={style.fieldGap}
              id="create-workspace-dps"
              titleText="Development Pace Scenario"
              label="Select a DPS"
              items={dpsList}
              selectedItem={selectedDpsItem}
              disabled={isLoading}
              itemToString={(item: DpsOption | null) => item?.label ?? ''}
              onChange={({
                selectedItem: item,
              }: {
                selectedItem: DpsOption | null;
              }) => {
                setSelectedDpsId(item?.id ?? null);
              }}
            />
          )}
        </>
      )}
    </Modal>
  );
};
